/**
 * Handlingene for én familie i systemadminpanelet (FF-11, #179): ny lenke for
 * en familieadmin, familiekode, brytere, AI-bryter og -budsjett (FF-13, #181),
 * suspender/gjenåpne og slett.
 *
 * Alt går bak re-autentisering (useReauthGate). Slett krever at familien er
 * suspendert og at koden skrives inn — to bevisste steg, så familien i
 * produksjon ikke kan slettes ved et uhell. D3: her vises bare navn,
 * brukernavn og nøkler, aldri familieinnhold.
 */
import { useId, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AI_BUDGET_TOKENS_MAX,
  isValidFamilyCode,
  normalizeFamilyCode,
  type SystemFamily,
  type SystemFamilyLink,
} from '@stell/shared';
import { Button, Switch } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { asApiError } from '../../lib/api';
import { OneTimeLink } from './OneTimeLink';
import { isReauthRequired } from './SystemReauthDialog';
import { SystemApi } from './systemApi';

type Guard = (action: () => Promise<void>) => Promise<void>;

const ERROR_KEY: Record<string, string> = {
  family_code_taken: 'system.errors.code_taken',
  smarthome_taken: 'system.errors.smarthome_taken',
  code_mismatch: 'system.errors.code_mismatch',
  not_suspended: 'system.errors.not_suspended',
};

export function FamilyActions({
  family,
  guard,
  onChanged,
  onDeleted,
}: {
  family: SystemFamily;
  guard: Guard;
  onChanged: () => void;
  onDeleted: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<{ link: SystemFamilyLink; name: string } | null>(null);
  const [code, setCode] = useState(family.code);
  const [confirmCode, setConfirmCode] = useState('');
  const [budget, setBudget] = useState(String(family.ai_budget_tokens_month));
  const codeId = useId();
  const budgetId = useId();
  const deleteId = useId();
  const admins = useQuery({
    queryKey: ['system', 'families', family.id, 'admins'],
    queryFn: () => SystemApi.admins(family.id),
  });

  /** Kjører et kall bak re-autentisering og viser feilen her. */
  function run(call: () => Promise<void>): void {
    setError(null);
    void guard(async () => {
      try {
        await call();
      } catch (e) {
        if (isReauthRequired(e)) throw e;
        setError(t(ERROR_KEY[asApiError(e).code] ?? 'system.errors.generic'));
      }
    });
  }

  /** En endring av familien: lista lastes på nytt etterpå. */
  const update = (call: () => Promise<unknown>) =>
    run(async () => {
      await call();
      onChanged();
    });

  const normalizedCode = normalizeFamilyCode(code);
  const codeChanged = normalizedCode !== family.code;
  // Bare hele tall fra 0 (ubegrenset) til taket i det delte skjemaet.
  const budgetValue = /^\d+$/.test(budget.trim()) ? Number(budget.trim()) : NaN;
  const budgetValid = Number.isSafeInteger(budgetValue) && budgetValue <= AI_BUDGET_TOKENS_MAX;
  const tokens = (n: number) => n.toLocaleString('nb-NO');

  return (
    <div data-testid={`system-family-actions-${family.code}`} className="flex flex-col gap-5 border-t border-card-line pt-4">
      {error ? (
        <p role="alert" data-testid="system-family-error" className="m-0 text-sm text-bad">
          {error}
        </p>
      ) : null}

      <section className="flex flex-col gap-2">
        <SectionLabel flush>{t('system.family.admins')}</SectionLabel>
        {(admins.data?.admins ?? []).map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-ink">
              {a.name} <span className="text-ink-2">· {a.username ?? '—'}</span>{' '}
              <span className="text-ink-2">· {t(a.has_password ? 'system.family.has_password' : 'system.family.no_password')}</span>
            </span>
            <Button
              variant="secondary"
              data-testid={`system-admin-link-${a.username ?? a.id}`}
              onClick={() =>
                run(async () => setLink({ link: await SystemApi.adminLink(family.id, a.id), name: a.name }))
              }
            >
              {t(a.has_password ? 'system.family.reset_link' : 'system.family.invite_link')}
            </Button>
          </div>
        ))}
        {link ? <OneTimeLink link={link.link} adminName={link.name} onDone={() => setLink(null)} /> : null}
      </section>

      <section className="flex flex-col gap-2">
        <SectionLabel as="label" htmlFor={codeId} flush>
          {t('system.family.code')}
        </SectionLabel>
        <div className="flex flex-wrap gap-3">
          <input
            id={codeId}
            data-testid="system-family-code"
            className="field max-w-xs grow"
            value={code}
            autoCapitalize="none"
            spellCheck={false}
            onChange={(e) => setCode(e.target.value)}
          />
          <Button
            variant="secondary"
            data-testid="system-family-code-save"
            disabled={!codeChanged || !isValidFamilyCode(normalizedCode)}
            onClick={() => update(() => SystemApi.updateFamily(family.id, { code: normalizedCode }))}
          >
            {t('system.family.code_save')}
          </Button>
        </div>
        <span className="text-sm text-ink-2">{t('system.family.code_warning')}</span>
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel flush>{t('system.family.features')}</SectionLabel>
        {(['smarthome_enabled', 'feedback_enabled'] as const).map((flag) => (
          <div key={flag} className="flex items-center justify-between gap-3">
            <span id={`${codeId}-${flag}`} className="text-sm text-ink">
              {t(`system.family.${flag}`)}
            </span>
            <Switch
              checked={family[flag]}
              labelledBy={`${codeId}-${flag}`}
              data-testid={`system-family-${flag}`}
              onChange={(next) => update(() => SystemApi.updateFamily(family.id, { [flag]: next }))}
            />
          </div>
        ))}
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel flush>{t('system.family.ai')}</SectionLabel>
        <div className="flex items-center justify-between gap-3">
          <span id={`${codeId}-ai_enabled`} className="text-sm text-ink">
            {t('system.family.ai_enabled')}
          </span>
          <Switch
            checked={family.ai_enabled}
            labelledBy={`${codeId}-ai_enabled`}
            data-testid="system-family-ai_enabled"
            onChange={(next) => update(() => SystemApi.updateFamily(family.id, { ai_enabled: next }))}
          />
        </div>
        <label htmlFor={budgetId} className="text-sm text-ink">
          {t('system.family.ai_budget')}
        </label>
        <div className="flex flex-wrap gap-3">
          <input
            id={budgetId}
            data-testid="system-family-ai-budget"
            className="field max-w-xs grow"
            inputMode="numeric"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
          />
          <Button
            variant="secondary"
            data-testid="system-family-ai-budget-save"
            disabled={!budgetValid || budgetValue === family.ai_budget_tokens_month}
            onClick={() => update(() => SystemApi.updateFamily(family.id, { ai_budget_tokens_month: budgetValue }))}
          >
            {t('system.family.ai_budget_save')}
          </Button>
        </div>
        <span data-testid="system-family-ai-usage" className="text-sm text-ink-2">
          {family.ai_budget_tokens_month > 0
            ? t('system.family.ai_budget_hint_limited', {
                used: tokens(family.ai_tokens_month),
                budget: tokens(family.ai_budget_tokens_month),
              })
            : t('system.family.ai_budget_hint', { used: tokens(family.ai_tokens_month) })}
        </span>
      </section>

      <section className="flex flex-col gap-2">
        <SectionLabel flush>{t('system.family.status')}</SectionLabel>
        {family.status === 'suspended' ? (
          <Button
            variant="secondary"
            className="self-start"
            data-testid="system-family-reactivate"
            onClick={() => update(() => SystemApi.reactivate(family.id))}
          >
            {t('system.family.reactivate')}
          </Button>
        ) : (
          <>
            <span className="text-sm text-ink-2">{t('system.family.suspend_hint')}</span>
            <Button
              variant="danger"
              className="self-start"
              data-testid="system-family-suspend"
              onClick={() => update(() => SystemApi.suspend(family.id))}
            >
              {t('system.family.suspend')}
            </Button>
          </>
        )}
      </section>

      {family.status === 'suspended' ? (
        <section className="flex flex-col gap-2">
          <SectionLabel as="label" htmlFor={deleteId} flush>
            {t('system.family.delete')}
          </SectionLabel>
          <span className="text-sm text-ink-2">{t('system.family.delete_hint', { code: family.code })}</span>
          <div className="flex flex-wrap gap-3">
            <input
              id={deleteId}
              data-testid="system-family-delete-code"
              className="field max-w-xs grow"
              value={confirmCode}
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setConfirmCode(e.target.value)}
            />
            <Button
              variant="danger"
              data-testid="system-family-delete"
              disabled={normalizeFamilyCode(confirmCode) !== family.code}
              onClick={() => run(async () => {
                await SystemApi.deleteFamily(family.id, confirmCode);
                onDeleted();
              })}
            >
              {t('system.family.delete_submit')}
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
